import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { PropsWithChildren } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import {
  CurrentPackagesModalAtom,
  goToDetailPageAtom,
  goToOrderPageAtom,
} from "@/features/Packages/CurrentPackagesModal/atoms";
import { ModalDetailState } from "@/features/Packages/CurrentPackagesModal/types";
import { ProfileStackNavigatorParamList } from "@/navigators/ProfileStackNavigator/types";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { StatusWarningIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { GetCardIconFromBrand } from "@whoosh/features/src/Square";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { convertDateStringToObj } from "@whoosh/utilities/src/dates";

import { useGetOutstandingOrders } from "../../../hooks/useGetOutstandingOrders";
import { useGetBillingPeriod } from "../hooks/useGetBillingPeriod";
import { useGetNextPaymentDate } from "../hooks/useGetNextPaymentDate";
import { useGetPriceMap } from "../hooks/useGetPriceMap";
import { CanceledPackageBanner } from "./CanceledPackageBanner";
import { PackageDetailBilling_memberPackage$key } from "./__generated__/PackageDetailBilling_memberPackage.graphql";

type Props = {
  memberPackageRef: PackageDetailBilling_memberPackage$key | null | undefined;
};
export const PackageDetailBilling = ({ memberPackageRef }: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const navigation =
    useNavigation<NavigationProp<ProfileStackNavigatorParamList>>();
  const memberPackage = useFragment(
    PackageDetailBillingFragment,
    memberPackageRef
  );

  const outstandingOrders = useGetOutstandingOrders(memberPackage);
  const hasMultipleOutstandingOrders = outstandingOrders.length > 1;
  const outstandingOrder = hasMultipleOutstandingOrders
    ? null
    : outstandingOrders[0];
  const goToOrderPage = useSetAtom(goToOrderPageAtom);
  const goToDetailPage = useSetAtom(goToDetailPageAtom);
  const currentState = useAtomValue(CurrentPackagesModalAtom);

  const billingPeriod = useGetBillingPeriod(memberPackage);
  const nextPaymentDate = useGetNextPaymentDate(memberPackage);
  const squareCard = memberPackage?.squareCard;

  const pricingMap = useGetPriceMap(memberPackage);
  const monthlyPrice =
    pricingMap && pricingMap["MONTHLY"] ? pricingMap["MONTHLY"] : null;
  const oneTimePrice =
    pricingMap && pricingMap["ONE_TIME"] ? pricingMap["ONE_TIME"] : null;

  const isCancelled = !!memberPackage?.cancelledOn;

  const hasMonthly = monthlyPrice !== null;
  const hasOneTime = oneTimePrice !== null;

  const onCompletePaymentPress = () => {
    if (outstandingOrder && memberPackage) {
      if (Platform.OS === "web") {
        goToOrderPage({
          orderId: outstandingOrder,
          packageId: memberPackage?.id || "",
          onBack: () => {
            goToDetailPage({ ...(currentState as ModalDetailState) });
          },
        });
      } else {
        if (memberPackage?.package.__typename === "MembershipPackage") {
          navigation.navigate("Memberships", {
            screen: "MembershipOrder",
            params: {
              orderId: outstandingOrder,
              membershipId: memberPackage.id,
            },
          });
        } else {
          navigation.navigate("Packages", {
            screen: "PackageOrder",
            params: {
              orderId: outstandingOrder,
              packageId: memberPackage.id,
            },
          });
        }
      }
    }
  };

  return (
    <VStack gap={4}>
      {outstandingOrder && !isCancelled ? (
        <InfoBanner
          message={t`Your account has an outstanding balance. Please complete your payment to continue enjoying your services.`}
          colorTheme="ERROR"
          action={{
            label: t`Complete Payment`,
            handler: onCompletePaymentPress,
          }}
        />
      ) : null}
      {hasMultipleOutstandingOrders ? (
        <InfoBanner
          message={t`Account is delinquent. Please contact the club to get your services reinstated.`}
          colorTheme="ERROR"
          leadingIcon={StatusWarningIcon}
        />
      ) : null}
      <VStack gap={2}>
        {hasMonthly ? (
          <Row>
            <Text>{t`Price`}</Text>
            <HStack>
              <Text>{`${currencySymbol}${ConvertCentsToDollars(
                monthlyPrice
              )}`}</Text>
              <Text color="text-secondary">{t` / month`}</Text>
            </HStack>
          </Row>
        ) : null}
        {hasOneTime ? (
          <Row>
            <Text>{hasMonthly ? "" : t`Price`}</Text>
            <Text>{`${currencySymbol}${ConvertCentsToDollars(
              oneTimePrice
            )}`}</Text>
          </Row>
        ) : null}
      </VStack>
      {billingPeriod ? (
        <Row>
          <Text>{t`Billing Period`}</Text>
          <Text>{billingPeriod}</Text>
        </Row>
      ) : null}
      {nextPaymentDate && hasMonthly && !isCancelled ? (
        <Row>
          <Text>{t`Next Payment`}</Text>
          <Text>
            {format(convertDateStringToObj(nextPaymentDate), "MMM do, yyyy")}
          </Text>
        </Row>
      ) : null}
      {squareCard || hasMonthly ? (
        <Row>
          <Text
            color={squareCard ? "text-primary" : "text-error"}
          >{t`Payment Method`}</Text>
          {squareCard ? (
            <HStack alignItems="center" gap={1}>
              {GetCardIconFromBrand(squareCard.cardBrand)}
              <Text>{`•••• ${squareCard?.lastFour}`}</Text>
            </HStack>
          ) : (
            <Text color="text-error">{t`Missing`}</Text>
          )}
        </Row>
      ) : null}
      {isCancelled ? (
        <CanceledPackageBanner memberPackageRef={memberPackage} />
      ) : null}
    </VStack>
  );
};

const Row = ({ children }: PropsWithChildren) => {
  return (
    <HStack justifyContent="space-between" alignItems="center">
      {children}
    </HStack>
  );
};

const PackageDetailBillingFragment = graphql`
  fragment PackageDetailBilling_memberPackage on MemberPackage {
    id
    ...useGetOutstandingOrders_memberPackage
    ...useGetPriceMap_memberPackage
    ...useGetBillingPeriod_memberPackage
    ...useGetNextPaymentDate_memberPackage
    ...CanceledPackageBanner_memberPackage
    package {
      __typename
    }
    cancelledOn
    squareCard {
      cardBrand
      lastFour
      expMonth
      expYear
    }
  }
`;
